import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from './Header';
import Pagination from './Pagination';
import logo from './images/icons/logo.JPG';
import {
  Search,
  Filter,
  Download,
  Eye,
  Mail,
  CheckCircle,
  MapPin,
  Building2,
  DollarSign,
  Calendar,
  User,
  Phone,
  Send,
  FileText
} from 'lucide-react';

const ModalViewTenants = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  
  let userCompanyID = localStorage.getItem('companyID');
  if(userCompanyID === 'Admin') {
    userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID);

  const [tenantsData, setTenantsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tenantStatement, setTenantStatement] = useState({ loopedItems: [], nonLoopedItems: [] });

  useEffect(() => {
    fetchDataForTableHousesTenants();
  }, []);

  useEffect(() => {
    // Filter data whenever search value changes
    if (searchValue.trim() === '') {
      setFilteredData(tenantsData);
    } else {
      const filtered = tenantsData.filter(tenant =>
        Object.values(tenant).some(value =>
          value?.toString().toLowerCase().includes(searchValue.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [searchValue, tenantsData]);

  const fetchDataForTableHousesTenants = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('apiType', 'fetchViewHousesTenants');
    formData.append('companyID', userCompanyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      const processedData = response.data.map(row => ({
        ...row,
        amountPaid: parseFloat(row.amountPaid) || 0
      }));
      setTenantsData(processedData);
      setFilteredData(processedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const formatCurrencyTable = (amount) => {
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    return new Intl.NumberFormat('en-ZM', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const tableRows = [];

    filteredData.forEach((row, index) => {
      tableRows.push([
        index + 1,
        row.businessLocationName,
        row.block,
        row.houseNumber,
        row.name,
        row.dateContractSigned,
        row.lastDatePaid,
        formatCurrencyTable(row.rentalPrice),
        formatCurrencyTable(row.amountPaid)
      ]);
    });

    doc.autoTable({
      head: [['SN', 'Location', 'Block', 'House #', 'Name', 'Contract Date', 'Last Paid', 'Rental Price', 'Amount Paid']],
      body: tableRows,
      margin: { top: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      }
    });

    doc.save('tenants-report.pdf');
  };

  const calculateTotals = (data) => {
    const totals = data.reduce((acc, tenant) => {
      acc.rentalPrice += parseFloat(tenant.rentalPrice) || 0;
      acc.amountPaid += parseFloat(tenant.amountPaid) || 0;
      acc.totalTenants += 1;
      return acc;
    }, { rentalPrice: 0, amountPaid: 0, totalTenants: 0 });
    
    return totals;
  };

  const viewTenantDetails = async (tenantId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'getTenantStatement');
      formData.append('id', tenantId);
      
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setTenantStatement(response.data);
      setSelectedTenant(response.data.nonLoopedItems?.[0]);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    }
  };

  const sendLoginCredentials = async (tenantId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailHousesLoginCredentials');
      formData.append('id', tenantId);
      
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      alert('Login credentials sent successfully');
    } catch (error) {
      alert('Error sending login credentials');
    }
  };

  const confirmForPaymentReminders = async (tenantId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'comfirmClientAvailability');
      formData.append('customerID', tenantId);
      formData.append('customerType', 'Houses');
      
      const response = await axios.post(`${server_app_api_php}/server/payment_remindersEmailList.php`, formData);
      alert('Confirmed for automated payment reminders');
    } catch (error) {
      alert('Error confirming for payment reminders');
    }
  };

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const totals = calculateTotals(tenantsData);
  const filteredTotals = calculateTotals(filteredData);

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">View Tenants</h1>
                    <p className="text-gray-600">Manage and view all rental tenants</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Tenants</p>
                      <p className="text-2xl font-bold text-gray-900">{totals.totalTenants}</p>
                    </div>
                    <User className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Rental Value</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.rentalPrice)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Amount Paid</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.amountPaid)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search tenants..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
                      />
                    </div>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5 entries</option>
                      <option value="10">10 entries</option>
                      <option value="25">25 entries</option>
                      <option value="50">50 entries</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {filteredData.length} tenants
                      {searchValue && ` (filtered)`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-4 text-gray-500">Loading tenants data...</p>
                  </div>
                ) : (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-blue-500 to-purple-600">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">SN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Block</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">House #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <User className="w-4 h-4 inline mr-1" />
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Contract Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Last Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                           
                            Rent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            
                            Amount Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentRows.map((tenant, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstRow + index + 1}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                                {tenant.businessLocationName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.block}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                              {tenant.houseNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <User className="w-4 h-4 text-gray-400 mr-2" />
                                {tenant.name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {tenant.dateContractSigned}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {tenant.lastDatePaid}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(tenant.rentalPrice)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                              {formatCurrency(tenant.amountPaid)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => viewTenantDetails(tenant.tenantsID)}
                                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>View</span>
                                </button>
                                <button
                                  onClick={() => sendLoginCredentials(tenant.tenantsID)}
                                  className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  <Mail className="w-4 h-4" />
                                  <span>Send Login</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                            Filtered Totals:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.rentalPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.amountPaid)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      totalItems={filteredData.length}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tenant Details Modal */}
        {showModal && selectedTenant && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              
              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white">Tenant Statement</h3>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                <div className="px-6 py-6">
                  {/* Tenant Information */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Tenant Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Tenant Name</p>
                        <p className="font-medium text-gray-900">{selectedTenant.name}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Email Address</p>
                        <p className="font-medium text-gray-900">{selectedTenant.tenantEmail}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-900">{selectedTenant.tenantPhone}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">House Number</p>
                        <p className="font-medium text-gray-900">{selectedTenant.houseNumber}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Financial Summary */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-xl">
                        <p className="text-sm text-green-600">Rental Price</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedTenant.rentalPrice)}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-600">Total Paid</p>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedTenant.rentalsTotalAmountPaid)}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600">Last Payment Date</p>
                        <p className="text-2xl font-bold text-gray-700">{selectedTenant.lastDatePaid}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment History */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Payment History</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SN</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tenantStatement.loopedItems && tenantStatement.loopedItems.length > 0 ? (
                            tenantStatement.loopedItems.map((payment, index) => (
                              <tr key={index}>
                                <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{payment.datePaid}</td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {formatCurrency(payment.amountPaid)}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                    Paid
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="px-4 py-3 text-sm text-gray-500 text-center">
                                No payment history available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => confirmForPaymentReminders(selectedTenant.tenantsID)}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Confirm for Reminders
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ModalViewTenants;